/** Split screen — terminal + browse/search pane beside each other. */
import { buildLeadsPageUrl } from './assistant-search.js';

const STORAGE_KEY = 'mysky_assistant_split';
const PROXY_BROWSE = '/api/browse';
const GOOGLE_SEARCH = '/api/google-search';

const SEARCH_ENGINE_HOSTS = new Set([
  'google.com', 'google.lk', 'google.co.uk', 'bing.com', 'yahoo.com', 'duckduckgo.com',
]);

function googleSearchUrl(query) {
  return `${GOOGLE_SEARCH}?q=${encodeURIComponent(query)}`;
}

let ctx = { onOpenLead: null };
let splitOpen = false;
let activeTab = 'leads';
let browseMessageBound = false;

function $(id) {
  return document.getElementById(id);
}

function ensureDom() {
  return !!($('assistantSplitPane') && $('assistantSplitToggle'));
}

function setTab(tab) {
  activeTab = tab === 'web' ? 'web' : 'leads';
  document.querySelectorAll('.assistant-split-tab').forEach((btn) => {
    btn.classList.toggle('active', btn.dataset.splitTab === activeTab);
  });
  document.querySelectorAll('.assistant-split-panel').forEach((panel) => {
    panel.classList.toggle('hidden', panel.dataset.splitPanel !== activeTab);
  });
}

function defaultLeadsUrl() {
  return '/leads/?assistant=1&tab=browse&dept_scope=all';
}

function loadLeadsFrame(url) {
  const frame = $('assistantSplitLeadsFrame');
  if (!frame) return;
  frame.src = url || defaultLeadsUrl();
}

function looksLikeUrl(raw) {
  const q = String(raw || '').trim();
  if (!q) return false;
  if (/^https?:\/\//i.test(q)) return true;
  if (q.startsWith('/')) return true;
  if (/^www\./i.test(q)) return true;
  return /^[\w.-]+\.[a-z]{2,}(?:[/:?#]|$)/i.test(q);
}

function normalizeUrl(raw) {
  const q = String(raw || '').trim();
  if (!q) return '';
  if (/^https?:\/\//i.test(q)) return q;
  if (q.startsWith('/')) return new URL(q, location.origin).href;
  return `https://${q}`;
}

function isSameOrigin(url) {
  try {
    return new URL(url).origin === location.origin;
  } catch {
    return false;
  }
}

function browseProxySrc(targetUrl) {
  return `${PROXY_BROWSE}?url=${encodeURIComponent(targetUrl)}`;
}

function isSearchEngineUrl(url) {
  try {
    const host = new URL(url).hostname.replace(/^www\./, '').toLowerCase();
    for (const engine of SEARCH_ENGINE_HOSTS) {
      if (host === engine || host.endsWith(`.${engine}`)) return true;
    }
    return false;
  } catch {
    return false;
  }
}

function extractSearchQuery(url) {
  try {
    const u = new URL(url);
    const q = u.searchParams.get('q') || u.searchParams.get('query') || u.searchParams.get('p');
    return q?.trim() || null;
  } catch {
    return null;
  }
}

function hideWebFrame() {
  const frame = $('assistantSplitWebFrame');
  if (!frame) return;
  frame.classList.add('hidden');
  frame.src = 'about:blank';
}

function showWebHelp() {
  hideWebFrame();
  const view = $('assistantSplitWebView');
  if (!view) return;
  view.innerHTML = [
    '<div class="assistant-split-web-card">',
    '<p class="assistant-split-web-title">Web panel</p>',
    '<p class="assistant-split-web-copy">Type a <strong>search phrase</strong> or paste a website URL — pages load here beside the terminal.</p>',
    '<p class="assistant-split-web-copy">Examples: <code>emirates.com</code> · <code>Sri Lanka visa requirements</code></p>',
    '<p class="assistant-split-web-hint">Keyword searches use official Google (via Programmable Search). Site URLs open in the panel via proxy.</p>',
    '</div>',
  ].join('');
}


function loadInWebPanel(targetUrl) {
  const frame = $('assistantSplitWebFrame');
  const view = $('assistantSplitWebView');
  if (!frame) return;

  if (view) view.innerHTML = '<p class="assistant-split-web-loading">Loading page…</p>';
  frame.classList.remove('hidden');
  frame.onload = () => {
    if (view) view.innerHTML = '';
  };

  let src;
  if (targetUrl.startsWith(`${GOOGLE_SEARCH}?`) || isSameOrigin(targetUrl)) {
    src = targetUrl;
  } else {
    src = browseProxySrc(targetUrl);
  }
  if (src.startsWith('http://') || src.startsWith('https://')) {
    src = browseProxySrc(targetUrl);
  }
  frame.src = src;
}

function loadWebSearch(query) {
  const input = $('assistantSplitUrl');
  const q = String(query || '').trim();
  if (input) input.value = q;

  if (!q) {
    showWebHelp();
    return;
  }

  if (looksLikeUrl(q)) {
    const url = normalizeUrl(q);
    if (isSearchEngineUrl(url)) {
      const embedded = extractSearchQuery(url);
      loadInWebPanel(googleSearchUrl(embedded || ''));
      return;
    }
    loadInWebPanel(url);
    return;
  }

  loadInWebPanel(googleSearchUrl(q));
}

function onBrowseMessage(event) {
  if (event.origin !== location.origin) return;
  if (event.data?.type !== 'mysky-browse-url') return;
  const input = $('assistantSplitUrl');
  if (input && event.data.url) input.value = event.data.url;
}

export function isSplitOpen() {
  return splitOpen;
}

export function setSplitOpen(on, { silent = false } = {}) {
  if (!ensureDom()) return;
  splitOpen = !!on;
  localStorage.setItem(STORAGE_KEY, splitOpen ? '1' : '0');
  $('assistantApp')?.classList.toggle('split-open', splitOpen);
  $('assistantSplitPane')?.classList.toggle('hidden', !splitOpen);
  const btn = $('assistantSplitToggle');
  if (btn) {
    btn.classList.toggle('is-active', splitOpen);
    btn.setAttribute('aria-pressed', splitOpen ? 'true' : 'false');
  }
  if (splitOpen) {
    if (!$('assistantSplitLeadsFrame')?.src || $('assistantSplitLeadsFrame').src === 'about:blank') {
      loadLeadsFrame(defaultLeadsUrl());
    }
    const frame = $('assistantSplitWebFrame');
    const onBlank = !frame?.src || frame.src === 'about:blank' || frame.classList.contains('hidden');
    if (onBlank && !$('assistantSplitWebView')?.innerHTML.trim()) {
      showWebHelp();
    }
    setTab(activeTab);
  }
  if (!silent && typeof ctx.onToggle === 'function') {
    ctx.onToggle(splitOpen);
  }
}

export function toggleSplit() {
  setSplitOpen(!splitOpen);
}

/** Open split and show leads filtered from terminal search syntax. */
export function openSplitLeadsSearch(rawQuery, { agentId, viewAll } = {}) {
  const url = buildLeadsPageUrl(rawQuery, { agentId, viewAll });
  setSplitOpen(true, { silent: true });
  setTab('leads');
  loadLeadsFrame(url);
}

export function initAssistantSplit(config = {}) {
  ctx = { ...ctx, ...config };
  if (!ensureDom()) return;

  if (!browseMessageBound) {
    window.addEventListener('message', onBrowseMessage);
    browseMessageBound = true;
  }

  $('assistantSplitToggle')?.addEventListener('click', () => toggleSplit());
  $('assistantSplitClose')?.addEventListener('click', () => setSplitOpen(false));

  document.querySelectorAll('.assistant-split-tab').forEach((btn) => {
    btn.addEventListener('click', () => setTab(btn.dataset.splitTab));
  });

  $('assistantSplitUrlForm')?.addEventListener('submit', (e) => {
    e.preventDefault();
    setTab('web');
    loadWebSearch($('assistantSplitUrl')?.value || '');
  });

  if (localStorage.getItem(STORAGE_KEY) === '1') {
    setSplitOpen(true, { silent: true });
  }
}

export function parseSplitCommand(raw) {
  const s = String(raw || '').trim().toLowerCase();
  if (s === 'split on' || s === 'split enable') return 'on';
  if (s === 'split off' || s === 'split disable' || s === 'split close') return 'off';
  if (s === 'split' || s === 'split toggle') return 'toggle';
  return null;
}
